import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import { Box, Paper, Skeleton, Typography, alpha, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

interface PilotLockedSectionProps {
  minHeight?: number;
  label?: string;
  preview?: "skeleton" | "chart" | "map";
}

const CHART_BARS = [38, 64, 46, 78, 58, 88, 70];
const MAP_MARKERS = [
  { top: "24%", left: "24%" },
  { top: "48%", left: "55%" },
  { top: "30%", left: "76%" },
  { top: "70%", left: "36%" },
];

function DummyChartPreview() {
  const theme = useTheme();

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Skeleton variant="text" width="36%" height={38} />
      <Box
        sx={{
          position: "relative",
          flex: 1,
          mt: 1.5,
          px: 2,
          pb: 1.5,
          display: "flex",
          alignItems: "flex-end",
          gap: { xs: 1.5, sm: 2.5 },
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderLeft: `1px solid ${theme.palette.divider}`,
          backgroundImage: `repeating-linear-gradient(to bottom, ${alpha(
            theme.palette.text.secondary,
            0.15,
          )} 0, ${alpha(theme.palette.text.secondary, 0.15)} 1px, transparent 1px, transparent 25%)`,
        }}
      >
        {CHART_BARS.map((height, index) => (
          <Box
            key={`${height}-${index}`}
            sx={{
              flex: 1,
              height: `${height}%`,
              minWidth: 12,
              borderRadius: "8px 8px 2px 2px",
              background: `linear-gradient(180deg, ${alpha(
                theme.palette.primary.light,
                0.95,
              )}, ${theme.palette.primary.main})`,
              boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.18)}`,
            }}
          />
        ))}
        <Box
          component="svg"
          viewBox="0 0 700 220"
          preserveAspectRatio="none"
          sx={{ position: "absolute", inset: "10% 2% 10% 2%", width: "96%", height: "80%" }}
        >
          <polyline
            points="15,176 120,126 225,148 330,74 435,106 540,38 685,66"
            fill="none"
            stroke={theme.palette.secondary.main}
            strokeWidth="9"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Box>
      </Box>
    </Box>
  );
}

function DummyMapPreview() {
  const theme = useTheme();

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Skeleton variant="text" width="30%" height={38} />
      <Box
        sx={{
          position: "relative",
          flex: 1,
          mt: 1.5,
          overflow: "hidden",
          borderRadius: 2.5,
          bgcolor: alpha(theme.palette.info.light, 0.18),
          backgroundImage: `linear-gradient(35deg, transparent 47%, ${alpha(
            theme.palette.background.paper,
            0.9,
          )} 48%, ${alpha(theme.palette.background.paper, 0.9)} 53%, transparent 54%), linear-gradient(120deg, transparent 45%, ${alpha(
            theme.palette.background.paper,
            0.8,
          )} 46%, ${alpha(theme.palette.background.paper, 0.8)} 51%, transparent 52%)`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: "55%",
            height: "55%",
            top: "-18%",
            right: "-8%",
            borderRadius: "44% 0 52% 48%",
            bgcolor: alpha(theme.palette.primary.light, 0.25),
          }}
        />
        {["12%", "42%", "72%"].map((top) => (
          <Box
            key={top}
            sx={{
              position: "absolute",
              top,
              left: "-5%",
              width: "110%",
              height: 12,
              bgcolor: alpha(theme.palette.background.paper, 0.72),
              transform: "rotate(-8deg)",
              border: `1px solid ${alpha(theme.palette.text.secondary, 0.1)}`,
            }}
          />
        ))}
        {MAP_MARKERS.map((marker, index) => (
          <Box
            key={`${marker.top}-${marker.left}`}
            sx={{
              position: "absolute",
              ...marker,
              width: 38,
              height: 38,
              display: "grid",
              placeItems: "center",
              borderRadius: "50%",
              color: index % 2 === 0 ? "primary.main" : "secondary.main",
              bgcolor: alpha(theme.palette.background.paper, 0.92),
              boxShadow: `0 5px 18px ${alpha(theme.palette.common.black, 0.18)}`,
            }}
          >
            <LocationOnRoundedIcon fontSize="medium" />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function PilotLockedSection({
  minHeight = 320,
  label,
  preview = "skeleton",
}: PilotLockedSectionProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const accessibleLabel = label ?? t("pilot.lockedTitle");

  return (
    <Paper
      elevation={0}
      sx={{ position: "relative", minHeight, overflow: "hidden", borderRadius: 3 }}
      aria-label={accessibleLabel}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          p: 3,
          filter: "blur(4px)",
          opacity: 0.82,
          pointerEvents: "none",
        }}
      >
        {preview === "chart" ? (
          <DummyChartPreview />
        ) : preview === "map" ? (
          <DummyMapPreview />
        ) : (
          <>
            <Skeleton variant="text" width="38%" height={42} />
            <Skeleton variant="rounded" height={Math.max(120, minHeight - 105)} sx={{ mt: 2 }} />
          </>
        )}
      </Box>
      <Box
        role="status"
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          px: 3,
          textAlign: "center",
          bgcolor: alpha(theme.palette.background.paper, 0.54),
          backdropFilter: "blur(1px)",
        }}
      >
        <Box
          sx={{
            width: 58,
            height: 58,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            color: "primary.main",
          }}
        >
          <LockOutlinedIcon fontSize="large" />
        </Box>
        <Typography variant="h6" fontWeight={700}>
          {accessibleLabel}
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 480 }}>
          {t("pilot.lockedMessage")}
        </Typography>
      </Box>
    </Paper>
  );
}

export default function PilotLockedView() {
  const { t } = useTranslation();
  return (
    <Box sx={{ py: 1 }}>
      <PilotLockedSection minHeight={560} label={t("pilot.lockedTitle")} />
    </Box>
  );
}
